import React, { useState, useEffect } from 'react';
import {
	PieChart,
	Pie,
	Cell,
	Tooltip,
	Legend,
	BarChart,
	Bar,
	XAxis,
	YAxis,
	CartesianGrid,
	ResponsiveContainer,
	LineChart,
	Line,
	LabelList,
} from 'recharts';
import TableLoading from '../../../components/TableLoading';
import { request } from '../../../services/utilities';
import SlidingPane from '../../../components/SlidingPane';
import { Card, Col, Row, Statistic } from 'antd';
import AnalysisBystaff from '../../../components/Modals/frontDesk/staffAnalyticsmodal';
import { TimeRangeDropdownAlt3 as TimeRangeDropdown } from '../../../components/TimeRangeDropdownAlt3';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ff6e54', '#ffc0cb', '#faad14'];

// const TimeRangeDropdown = ({ value, onChange, chartId }) => {
// 	return (
// 		<select
// 			value={value}
// 			onChange={e => onChange(e.target.value, chartId)}
// 			className="form-control form-control-sm rounded"
// 			style={{ width: '160px', marginBottom: '10px', borderRadius: '50px' }}
// 		>
// 			<option value="today">Today</option>
// 			<option value="pastWeek">Past Week</option>
// 			<option value="past30Days">Past 30 Days</option>
// 			<option value="lastMonth">Last Month</option>
// 			<option value="pastQuarter">Past Quarter</option>
// 			<option value="pastSixMonths">Past Six Months</option>
// 			<option value="pastYear">Past Year</option>
// 			<option value="lastYear">Last Year</option>
// 			<option value="pastThreeYears">Past Three Years</option>
// 			<option value="allTime">All Time</option>
// 		</select>
// 	);
// };

const StaffHmoDashboard = ({ selectedStaff }) => {
	const [timeRanges, setTimeRanges] = useState({
		allMetrics: 'past30Days',
		patientHmoByScheme: 'past30Days',
		patientHmoByPayment: 'past30Days',
		hmoByType: 'past30Days',
		topServices: 'past30Days',
		topBottomServices: 'past30Days',
		claimedNotClaimed: 'past30Days',
		topBottomServicesPayment: 'past30Days',
	});

	const [loadingStates, setLoadingStates] = useState({
		allMetrics: false,
		patientHmoByScheme: false,
		patientHmoByPayment: false,
		hmoByType: false,
		topServices: false,
		topBottomServices: false,
		claimedNotClaimed: false,
	});

	const [allMetrics, setAllMetrics] = useState({});
	const [patientHmoByScheme, setPatientHmoByScheme] = useState([]);
	const [patientHmoByPayment, setPatientHmoByPayment] = useState([]);
	const [hmoByType, setHmoByType] = useState([]);
	const [topServices, setTopServices] = useState([]);
	const [topBottomServices, setTopBottomServices] = useState([]);
	const [claimedNotClaimed, setClaimedNotClaimed] = useState([]);
	const [totalHmo, setTotalHmo] = useState();
	const [topServicesPayment, setTopServicesPayment] = useState([]);
	const [bottomServicesPayment, setBottomServicesPayment] = useState([]);
	const [modal, setModal] = useState(false);
	const [modalAlt, setModalAlt] = useState(false);

	const mapTimeRangeToApiFilter = range => {
		switch (range) {
			case 'today':
				return 'today';
			case 'pastWeek':
				return 'past-week';
			case 'past30Days':
				return 'past-month';
			case 'lastMonth':
				return 'last-month';
			case 'pastQuarter':
				return 'past-quarter';
			case 'pastSixMonths':
				return 'past-six-months';
			case 'pastYear':
				return 'past-year';
			case 'lastYear':
				return 'last-year';
			case 'pastThreeYears':
				return 'past-three-years';
			case 'allTime':
				return 'all-time';
			default:
				return 'past-month';
		}
	};

	const fetchAllMetrics = async range => {
		setLoadingStates(prev => ({ ...prev, allMetrics: true }));
		try {
			const dateFilter = mapTimeRangeToApiFilter(range);
			const res = await request(`analytics/hmo-status?dateFilter=${dateFilter}&staffId=${selectedStaff?.id}`, 'GET', true);
			const totalHmo = await request(`hmos/owners?page=1&limit=12`, 'GET', true);
			setTotalHmo(totalHmo?.totalPages || 0);
			setAllMetrics(...res?.result);
		} catch (error) {
			console.error('Failed to fetch all metrics:', error);
			setAllMetrics({});
		} finally {
			setLoadingStates(prev => ({ ...prev, allMetrics: false }));
		}
	};

	const fetchPatientHmoByScheme = async range => {
		setLoadingStates(prev => ({ ...prev, patientHmoByScheme: true }));
		const dateFilter = mapTimeRangeToApiFilter(range);
		try {
			const res = await request(`analytics/hmo-patients?dateFilter=${dateFilter}&staffId=${selectedStaff?.id}`, 'GET', true);
			const apiRespone = res.data.map(x => ({
				name: x?.hmoname,
				value: parseInt(x?.totalpatients, 10),
			}));
			setPatientHmoByScheme(apiRespone);
		} catch (error) {
			console.error('Failed to fetch HMO by scheme:', error);
			setPatientHmoByScheme([]);
		} finally {
			setLoadingStates(prev => ({ ...prev, patientHmoByScheme: false }));
		}
	};

	const fetchPatientHmoByPayment = async range => {
		setLoadingStates(prev => ({ ...prev, patientHmoByPayment: true }));
		const dateFilter = mapTimeRangeToApiFilter(range);
		try {
			const res = await request(`analytics/hmo-type?dateFilter=${dateFilter}&staffId=${selectedStaff?.id}`, 'GET', true);
			const apiRespone = res.data.map(x => ({
				name: x?.type,
				value: parseInt(x?.totalhmos, 10),
			}));
			setPatientHmoByPayment(apiRespone);
		} catch (error) {
			console.error('Failed to fetch HMO by payment:', error);
			setPatientHmoByPayment([]);
		} finally {
			setLoadingStates(prev => ({ ...prev, patientHmoByPayment: false }));
		}
	};

	const fetchHmoByType = async range => {
		const dateFilter = mapTimeRangeToApiFilter(range);
		setLoadingStates(prev => ({ ...prev, hmoByType: true }));
		try {
			const res = await request(`analytics/hmo-patients-type?dateFilter=${dateFilter}&staffId=${selectedStaff?.id}`, 'GET', true);
			const apiRespone = res.data.map(x => ({
				name: x?.type,
				value: parseInt(x?.totalpatients, 10),
			}));

			setHmoByType(apiRespone);
		} catch (error) {
			console.error('Failed to fetch HMO by type:', error);
			setHmoByType([]);
		} finally {
			setLoadingStates(prev => ({ ...prev, hmoByType: false }));
		}
	};

	const fetchTopServices = async range => {
		setLoadingStates(prev => ({ ...prev, topServices: true }));
		const dateFilter = mapTimeRangeToApiFilter(range);
		try {
			const res = await request(`analytics/hmo-top-services?dateFilter=${dateFilter}&staffId=${selectedStaff?.id}`, 'GET', true);
			const apiRespone = res.result.map(x => ({
				name: x?.service,
				source: x?.source,
				value: parseInt(x?.totaltransactions, 10),
			}));
			setTopServices(apiRespone);
		} catch (error) {
			console.error('Failed to fetch top services:', error);
			setTopServices([]);
		} finally {
			setLoadingStates(prev => ({ ...prev, topServices: false }));
		}
	};

	const fetchTopBottomServices = async range => {
		setLoadingStates(prev => ({ ...prev, topBottomServices: true }));
		const dateFilter = mapTimeRangeToApiFilter(range);
		try {
			const res = await request(`analytics/hmo-top-services?dateFilter=${dateFilter}&staffId=${selectedStaff?.id}`, 'GET', true);
			const apiRespone = res?.result?.map(x => ({
				name: x?.service,
				source: x?.source,
				value: parseInt(x?.totaltransactions, 10),
			}));

			const apiResponeBottom = res?.result?.map(x => ({
				name: x?.service || 'not-specified',
				source: x?.source,
				value: parseInt(x?.totaltransactions, 10),
			}));
			setTopBottomServices(apiResponeBottom);
			setTopServices(apiRespone);
		} catch (error) {
			console.error('Failed to fetch top/bottom services:', error);
			setTopBottomServices([]);
		} finally {
			setLoadingStates(prev => ({ ...prev, topBottomServices: false }));
		}
	};

	const fetchTopBottomServicesPayment = async range => {
		setLoadingStates(prev => ({ ...prev, topBottomServices: true }));
		const dateFilter = mapTimeRangeToApiFilter(range);
		try {
			const res = await request(`analytics/hmo-service-breakdown?dateFilter=${dateFilter}&staffId=${selectedStaff?.id}`, 'GET', true);
			const apiRespone = res?.result?.map(x => ({
				name: x?.name,
				value: parseInt(x?.totaltransactions, 10),
			}));

			const apiResponeBottom = res?.result?.map(x => ({
				name: x?.name,
				value: parseInt(x?.totaltransactions, 10),
			}));

			setTopServicesPayment(apiRespone);
			setBottomServicesPayment(apiResponeBottom);
		} catch (error) {
			console.error('Failed to fetch top/bottom services:', error);
			setTopBottomServices([]);
		} finally {
			setLoadingStates(prev => ({ ...prev, topBottomServices: false }));
		}
	};

	const fetchClaimedNotClaimed = async range => {
		setLoadingStates(prev => ({ ...prev, claimedNotClaimed: true }));
		const dateFilter = mapTimeRangeToApiFilter(range);
		try {
			const res = await request(`analytics/hmo-claim?dateFilter=${dateFilter}&staffId=${selectedStaff?.id}`, 'GET', true);
			const apiRespone = res?.result?.map(x => ({
				name: x?.month || x?.date || x?.day || x?.week || x?.year,
				claimed: x?.status ? parseInt(x?.count, 10) : 0,
				notClaimed: x?.status ? 0 : parseInt(x?.count, 10),
			}));
			setClaimedNotClaimed(apiRespone);
		} catch (error) {
			console.error('Failed to fetch claimed/not claimed:', error);
			setClaimedNotClaimed([]);
		} finally {
			setLoadingStates(prev => ({ ...prev, claimedNotClaimed: false }));
		}
	};

	useEffect(() => {
		fetchTopBottomServicesPayment(timeRanges.topBottomServicesPayment);
		fetchAllMetrics(timeRanges.allMetrics);
		fetchPatientHmoByScheme(timeRanges.patientHmoByScheme);
		fetchPatientHmoByPayment(timeRanges.patientHmoByPayment);
		fetchHmoByType(timeRanges.hmoByType);
		fetchTopServices(timeRanges.topServices);
		fetchTopBottomServices(timeRanges.topBottomServices);
		fetchClaimedNotClaimed(timeRanges.claimedNotClaimed);
	}, []);

	const handleTimeRangeChange = (range, chartId) => {
		setTimeRanges(prev => ({ ...prev, [chartId]: range }));
		switch (chartId) {
			case 'allMetrics':
				fetchAllMetrics(range);
				break;
			case 'patientHmoByScheme':
				fetchPatientHmoByScheme(range);
				break;
			case 'patientHmoByPayment':
				fetchPatientHmoByPayment(range);
				break;
			case 'hmoByType':
				fetchHmoByType(range);
				break;
			case 'topServices':
				fetchTopServices(range);
				break;
			case 'topBottomServices':
				fetchTopBottomServices(range);
				break;
			case 'topBottomServicesPayment':
				fetchTopBottomServicesPayment(range);
				break;
			case 'claimedNotClaimed':
				fetchClaimedNotClaimed(range);
				break;
			default:
				break;
		}
	};

	const CustomTooltip = ({ active, payload, label }) => {
		if (active && payload && payload.length) {
			const data = payload[0]?.payload; // full data object of the hovered bar

			return (
				<div
					className="custom-tooltip"
					style={{
						backgroundColor: '#fff',
						padding: '10px',
						border: '1px solid #ccc',
						borderRadius: '4px',
						boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
					}}
				>
					<p className="label" style={{ fontWeight: 'bold', marginBottom: '5px' }}>
						{data?.name || label}
					</p>
					{payload.map((entry, index) => (
						<p key={`item-${index}`} style={{ color: entry.color || '#000', margin: '3px 0' }}>
							{`${entry.name}: ${entry.value?.toLocaleString()}`}
						</p>
					))}
					{data?.source && <p style={{ marginTop: '5px', fontStyle: 'italic', color: '#555' }}>Source: {data.source}</p>}
				</div>
			);
		}

		return null;
	};

	const renderCustomLabel = ({ name, value, percent }) => {
		return `${name}: ${value} (${(percent * 100).toFixed(1)}%)`;
	};

	const renderCustomLabelAlt = ({ x, y, value, fill }) => {
		return (
			<text x={x} y={y} dy={-6} textAnchor="middle" fill={fill || '#333'} fontSize={12}>
				{value?.toLocaleString()}
			</text>
		);
	};

	const formatBarLabel = ({ x, y, value, width }) => (
		<text x={x + width / 2} y={y - 6} textAnchor="middle" fill="#333" fontSize={12}>
			{value?.toLocaleString()}
		</text>
	);

	return (
		<div className="content-i">
			<div className="content-box">
				<div className="row">
					<div className="col-sm-12">
						<div className="element-wrapper"></div>
					</div>
				</div>

				{/* All Metrics */}
				<div className="row">
					<div className="col-sm-12">
						<div className="element-wrapper">
							<h6 className="element-header">HMO Transaction Metrics</h6>
							<div className="d-flex justify-content-end w-100">
								<TimeRangeDropdown value={timeRanges.allMetrics} onChange={handleTimeRangeChange} chartId="allMetrics" />
							</div>
							<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
								Overview of key HMO metrics, including total HMOs, transactions, and approval statuses.
							</p>
							<div className="element-box">
								{loadingStates.allMetrics ? (
									<TableLoading />
								) : (
									<div className="el-tablo">
										<div className="row">
											<div className="col-4">
												<div className="value" style={{ fontSize: '24px', color: '#0088FE' }}>
													{Number(totalHmo || 0).toLocaleString()}
												</div>
												<div className="label">Total HMOs</div>
											</div>
											<div className="col-4">
												<div className="value" style={{ fontSize: '24px', color: '#00C49F' }}>
													{Number(allMetrics.totaltransactions || 0)?.toLocaleString()}
												</div>
												<div className="label">Total Transactions</div>
											</div>
											<div className="col-4">
												<div className="value" style={{ fontSize: '24px', color: '#FFBB28' }}>
													{Number(allMetrics.approvedTransactions || 0).toLocaleString()}
												</div>
												<div className="label">Approved Transactions</div>
											</div>
											<div className="col-4">
												<div className="value" style={{ fontSize: '24px', color: '#FF8042' }}>
													{Number(allMetrics.pendingTransactions || 0)?.toLocaleString()}
												</div>
												<div className="label">Pending Transactions</div>
											</div>
											<div className="col-4">
												<div className="value" style={{ fontSize: '24px', color: '#8884d8' }}>
													{Number(allMetrics.approvedWithCode || 0).toLocaleString()}
												</div>
												<div className="label">Approved with Code</div>
											</div>
											<div className="col-4">
												<div className="value" style={{ fontSize: '24px', color: '#82ca9d' }}>
													{Number(allMetrics?.approvedWithNoCode || 0).toLocaleString()}
												</div>
												<div className="label">Approved without Code</div>
											</div>
										</div>
									</div>
								)}
							</div>
						</div>
					</div>
				</div>

				{/* Patient HMO Distribution by Scheme */}
				<div className="row">
					<div className="col-12">
						<div className="element-wrapper">
							<h6 className="element-header">Patient HMO Distribution by Scheme</h6>
							<div className="d-flex justify-content-end w-100">
								<TimeRangeDropdown value={timeRanges.patientHmoByScheme} onChange={handleTimeRangeChange} chartId="patientHmoByScheme" />
							</div>
							<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
								Distribution of patients across different HMO schemes, highlighting coverage preferences.
							</p>
							<div className="element-box">
								{loadingStates.patientHmoByScheme ? (
									<TableLoading />
								) : patientHmoByScheme.length > 0 ? (
									<div style={{ height: '400px' }}>
										<ResponsiveContainer>
											<BarChart data={patientHmoByScheme} margin={{ top: 20, right: 30, left: 20, bottom: 40 }}>
												<CartesianGrid strokeDasharray="3 3" />
												<XAxis
													dataKey={'name'}
													angle={-45}
													textAnchor="end"
													fontSize={12}
													interval={0}
													tickFormatter={value => {
														const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
														const capitalized = truncated
															.toLowerCase()
															.split(' ')
															.map(word => word.charAt(0).toUpperCase() + word.slice(1))
															.join(' ');
														return capitalized;
													}}
												/>
												<YAxis domain={[0, 'auto']} />
												<Tooltip content={<CustomTooltip />} />
												<Legend wrapperStyle={{ marginBottom: '-45px' }} />
												<Bar dataKey="value" fill="#8884d8" name="Patients HMO destribution by Scheme">
													{patientHmoByScheme.map((entry, index) => (
														<Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
													))}
													<LabelList
														dataKey="value"
														position="top"
														style={{ fontSize: '12px', fill: '#333' }}
														formatter={value => `${value}`}
													/>
												</Bar>
											</BarChart>
										</ResponsiveContainer>
									</div>
								) : (
									<div className=" w-100 card d-flex justify-content-center align-items-center" style={{ height: '50px' }}>
										<p className=" mt-6" style={{ paddingTop: '50px' }}>
											No Record for the selected time
										</p>
									</div>
								)}
							</div>
						</div>
					</div>

					{/* Patient HMO Distribution by Payment Type */}
					<div className="col-sm-6">
						<div className="element-wrapper">
							<h6 className="element-header">Patient HMO Distribution by Payment Type</h6>
							<div className="d-flex justify-content-end w-100">
								<TimeRangeDropdown value={timeRanges.patientHmoByPayment} onChange={handleTimeRangeChange} chartId="patientHmoByPayment" />
							</div>
							<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
								Breakdown of HMO patients by payment type, aiding in financial and coverage analysis.
							</p>
							<div className="element-box">
								{loadingStates.patientHmoByPayment ? (
									<TableLoading />
								) : patientHmoByPayment.length > 0 ? (
									<div style={{ height: '300px' }}>
										<ResponsiveContainer>
											<PieChart>
												<Pie
													data={patientHmoByPayment}
													cx="50%"
													cy="50%"
													innerRadius={60}
													outerRadius={80}
													paddingAngle={5}
													dataKey="value"
													label={renderCustomLabel}
													labelLine
												>
													{patientHmoByPayment.map((entry, index) => (
														<Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
													))}
												</Pie>
												<Tooltip content={<CustomTooltip />} />
												<Legend />
											</PieChart>
										</ResponsiveContainer>
									</div>
								) : (
									<div className=" w-100 card d-flex justify-content-center align-items-center" style={{ height: '50px' }}>
										<p className=" mt-6" style={{ paddingTop: '50px' }}>
											No Record for the selected time
										</p>
									</div>
								)}
							</div>
						</div>
					</div>
					<div className="col-sm-6">
						<div className="element-wrapper">
							<h6 className="element-header">HMO Distribution by patient type </h6>
							<div className="d-flex justify-content-end w-100">
								<TimeRangeDropdown value={timeRanges.hmoByType} onChange={handleTimeRangeChange} chartId="hmoByType" />
							</div>
							<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
								Distribution of HMO plans by type, showing the prevalence of individual, family, and corporate plans.
							</p>
							<div className="element-box">
								{loadingStates.hmoByType ? (
									<TableLoading />
								) : hmoByType.length > 0 ? (
									<div style={{ height: '300px' }}>
										<ResponsiveContainer>
											<PieChart>
												<Pie
													data={hmoByType}
													cx="50%"
													cy="50%"
													innerRadius={60}
													outerRadius={80}
													paddingAngle={5}
													dataKey="value"
													label={renderCustomLabel}
													labelLine
												>
													{hmoByType.map((entry, index) => (
														<Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
													))}
												</Pie>
												<Tooltip content={<CustomTooltip />} />
												<Legend />
											</PieChart>
										</ResponsiveContainer>
									</div>
								) : (
									<div className=" w-100 card d-flex justify-content-center align-items-center" style={{ height: '50px' }}>
										<p className=" mt-6" style={{ paddingTop: '50px' }}>
											No Record for the selected time
										</p>
									</div>
								)}
							</div>
						</div>
					</div>
				</div>

				{/* Top 5 and Bottom 5 HMO Services */}
				<div className="row">
					<div className="col-sm-12">
						<div className="element-wrapper">
							<h6 className="element-header">HMO Services(Top 10 )</h6>
							<div className="d-flex justify-content-end w-100">
								<div className="d-flex justify-content-end w-100">
									<div>
										<div
											style={{ width: '100px', cursor: 'pointer', textAlign: 'center' }}
											onClick={() => setModal(true)}
											className="form-control form-control-sm text-white bg-primary rounded"
										>
											View All
										</div>
									</div>
									&nbsp;
									<TimeRangeDropdown value={timeRanges.topBottomServices} onChange={handleTimeRangeChange} chartId="topBottomServices" />
								</div>
							</div>
							<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
								Top 5 most utilized HMO services, highlighting high-demand areas.
							</p>
							<div className="element-box">
								{loadingStates.topBottomServices ? (
									<TableLoading />
								) : topServices.length > 0 ? (
									<div style={{ height: '600px' }}>
										<ResponsiveContainer>
											<BarChart data={topServices?.slice(0, 10)} margin={{ top: 20, right: 30, left: 20, bottom: 40 }}>
												<CartesianGrid strokeDasharray="3 3" />
												<XAxis
													dataKey={'name'}
													angle={-45}
													textAnchor="end"
													fontSize={12}
													interval={0}
													tickFormatter={value => {
														const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
														const capitalized = truncated
															.toLowerCase()
															.split(' ')
															.map(word => word.charAt(0).toUpperCase() + word.slice(1))
															.join(' ');
														return capitalized;
													}}
												/>
												<YAxis domain={[0, 'auto']} />
												<Tooltip content={<CustomTooltip />} />
												<Legend wrapperStyle={{ marginBottom: '-45px' }} />
												<Bar dataKey="value" fill="#8884d8" name="Requests">
													{topServices.map((entry, index) => (
														<Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
													))}
													<LabelList
														dataKey="value"
														position="top"
														style={{ fontSize: '10px', fill: '#333' }}
														formatter={value => `${value}`}
														content={formatBarLabel}
													/>
												</Bar>
											</BarChart>
										</ResponsiveContainer>
									</div>
								) : (
									<div className=" w-100 card d-flex justify-content-center align-items-center" style={{ height: '50px' }}>
										<p className=" mt-6" style={{ paddingTop: '50px' }}>
											No Record for the selected time
										</p>
									</div>
								)}
							</div>
						</div>
					</div>

					<div className="col-sm-12">
						<div className="element-wrapper">
							<h6 className="element-header"> HMO Services(Bottom 10)</h6>
							<div className="d-flex justify-content-end w-100">
								<div className="d-flex justify-content-end w-100">
									<div>
										<div
											style={{ width: '100px', cursor: 'pointer', textAlign: 'center' }}
											onClick={() => setModal(true)}
											className="form-control form-control-sm text-white bg-primary rounded"
										>
											View All
										</div>
									</div>
									&nbsp;
									<TimeRangeDropdown value={timeRanges.topBottomServices} onChange={handleTimeRangeChange} chartId="topBottomServices" />
								</div>
							</div>
							<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
								Least utilized HMO services, identifying areas for potential improvement.
							</p>
							<div className="element-box">
								{loadingStates.topBottomServices ? (
									<TableLoading />
								) : topBottomServices.length > 0 ? (
									<div style={{ width: '100%', height: 400 }}>
										<ResponsiveContainer>
											<BarChart data={topBottomServices?.slice(-10)} margin={{ top: 20, right: 30, left: 20, bottom: 40 }}>
												<CartesianGrid strokeDasharray="3 3" />
												<XAxis
													dataKey={'name'}
													angle={-45}
													textAnchor="end"
													fontSize={12}
													interval={0}
													tickFormatter={value => {
														const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
														const capitalized = truncated
															.toLowerCase()
															.split(' ')
															.map(word => word.charAt(0).toUpperCase() + word.slice(1))
															.join(' ');
														return capitalized;
													}}
												/>
												<YAxis domain={[0, 'auto']} />
												<Tooltip content={<CustomTooltip />} />
												<Legend wrapperStyle={{ marginBottom: '-45px' }} />
												<Bar dataKey="value" fill="#ff6e54" name="Requests">
													{topBottomServices.map((entry, index) => (
														<Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
													))}
													<LabelList
														dataKey="value"
														position="top"
														style={{ fontSize: '10px', fill: '#333' }}
														// formatter={value => `${value}`}
														content={formatBarLabel}
													/>
												</Bar>
											</BarChart>
										</ResponsiveContainer>
									</div>
								) : (
									<div className=" w-100 card d-flex justify-content-center align-items-center" style={{ height: '50px' }}>
										<p className=" mt-6" style={{ paddingTop: '50px' }}>
											No Record for the selected time
										</p>
									</div>
								)}
							</div>
						</div>
						<SlidingPane isOpen={modal}>
							<div className=" content-box">
								<div className=" d-flex justify-content-end w-100">
									<button className="btn btn-danger float-end" onClick={() => setModal(false)}>
										&times;
									</button>
								</div>

								<div className=" d-flex justify-content-end mt-5 t-5 align-items-center">
									<TimeRangeDropdown value={timeRanges.topBottomServices} onChange={handleTimeRangeChange} chartId="topBottomServices" />
								</div>
								<div className="row  w-100 " style={{ paddingLeft: '50px', paddingRight: '50px' }}>
									<div className="col-sm-12">
										<div className="element-wrapper">
											<h6 className="element-header">HMO Services </h6>

											<p className="chart-description card p-2 " style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
												High-level metrics showing overall metrics of reading over time.
											</p>
											<div className="element-box">
												{/* <TableLoading /> */}

												<Row gutter={16}>
													<Col span={8}>
														<Card>
															<Statistic title="Total" value={topBottomServices.length} suffix="" />
															<small className="text-muted">Total HMO Services</small>
														</Card>
													</Col>
													{/* <Col span={8}>
														<Card>
															<Statistic
																title="Total Revenue"
																value={transSummary?.totalRevenueFormatted} suffix=""
															/>

															<small className="text-muted">Cumulative Revenue From --- to --</small>
														</Card>
													</Col>
													<Col span={8}>
														<Card>
															<Statistic
																title="Total Transactions"
																value={transSummary?.totalTransactions} suffix=""
															/>
															<Statistic
																					title="Survey Coverage Rate"
																					value={coverageRate > 0 ? (coverageRate).toFixed(1) : 0}
																					suffix=""
																				/>
															<small className="text-muted">Cumulative Transactions From --- to --</small>
														</Card>
													</Col> */}
													{/* <Col span={6}>
																		<Card>
																			<Statistic title="Lowest Scoring Area" value={0} suffix="/3" />
																			<small className="text-muted">Needs attention</small>
																		</Card>
																	</Col> */}
												</Row>
											</div>
										</div>
									</div>
								</div>

								<div className=" " style={{ marginLeft: '50px', marginRight: '50px' }}>
									<p className="chart-description card p-2">
										This chart highlights HMO services, identifying areas for potential improvement.
									</p>
								</div>

								<div className=" " style={{ marginLeft: '50px', marginRight: '50px' }}>
									<div className=" card w-100 ">
										<div className="row">
											<div className="col-md-12 mb-4">
												<div className="card h-100">
													<div className="card-body">
														{loadingStates.topBottomServices ? (
															<TableLoading />
														) : topBottomServices.length > 0 ? (
															<div style={{ width: '100%', height: 'auto' }}>
																{topBottomServices?.length > 45 ? (
																	<ResponsiveContainer width="100%" height={topBottomServices?.length * 40}>
																		<BarChart
																			data={topBottomServices}
																			layout="vertical"
																			margin={{ top: 20, right: 30, left: 100, bottom: 20 }}
																		>
																			<CartesianGrid strokeDasharray="3 3" />

																			{/* Category labels on Y axis */}
																			<YAxis
																				type="category"
																				dataKey="name"
																				tick={{ fontSize: 12 }}
																				width={120}
																				tickFormatter={value => {
																					const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
																					const capitalized = truncated
																						.toLowerCase()
																						.split(' ')
																						.map(word => word.charAt(0).toUpperCase() + word.slice(1))
																						.join(' ');
																					return capitalized;
																				}}
																			/>

																			{/* Values on X axis */}
																			<XAxis type="number" domain={[0, 'auto']} />

																			<Tooltip content={<CustomTooltip />} />
																			<Legend />

																			<Bar dataKey="value" fill="#ff6e54" name="Requests">
																				{topBottomServices.map((entry, index) => (
																					<Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
																				))}
																				<LabelList
																					dataKey="value"
																					position="right" // 👈 switched from "top" to "right"
																					style={{ fontSize: '10px', fill: '#333' }}
																					content={formatBarLabel}
																				/>
																			</Bar>
																		</BarChart>
																	</ResponsiveContainer>
																) : (
																	<ResponsiveContainer>
																		<BarChart data={topBottomServices} margin={{ top: 20, right: 30, left: 20, bottom: 40 }}>
																			<CartesianGrid strokeDasharray="3 3" />
																			<XAxis
																				dataKey={'name'}
																				angle={-45}
																				textAnchor="end"
																				fontSize={12}
																				interval={0}
																				tickFormatter={value => {
																					const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
																					const capitalized = truncated
																						.toLowerCase()
																						.split(' ')
																						.map(word => word.charAt(0).toUpperCase() + word.slice(1))
																						.join(' ');
																					return capitalized;
																				}}
																			/>
																			<YAxis domain={[0, 'auto']} />
																			<Tooltip content={<CustomTooltip />} />
																			<Legend wrapperStyle={{ marginBottom: '-45px' }} />
																			<Bar dataKey="value" fill="#ff6e54" name="Requests">
																				{topBottomServices.map((entry, index) => (
																					<Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
																				))}
																				<LabelList
																					dataKey="value"
																					position="top"
																					style={{ fontSize: '10px', fill: '#333' }}
																					// formatter={value => `${value}`}
																					content={formatBarLabel}
																				/>
																			</Bar>
																		</BarChart>
																	</ResponsiveContainer>
																)}
															</div>
														) : (
															<div className=" w-100 card d-flex justify-content-center align-items-center" style={{ height: '50px' }}>
																<p className=" mt-6" style={{ paddingTop: '50px' }}>
																	No Record for the selected time
																</p>
															</div>
														)}
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</SlidingPane>
					</div>
				</div>

				{/* Claimed/Not Claimed Counts */}
				<div className="row">
					<div className="col-sm-12">
						<div className="element-wrapper">
							<h6 className="element-header">Claimed vs. Not Claimed Transactions</h6>
							<div className="d-flex justify-content-end w-100">
								<TimeRangeDropdown value={timeRanges.claimedNotClaimed} onChange={handleTimeRangeChange} chartId="claimedNotClaimed" />
							</div>
							<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
								Tracks the number of claimed versus unclaimed HMO transactions over time, highlighting claim processing efficiency.
							</p>
							<div className="element-box">
								{loadingStates.claimedNotClaimed ? (
									<TableLoading />
								) : claimedNotClaimed.length > 0 ? (
									<div style={{ width: '100%', height: 400 }}>
										<ResponsiveContainer>
											<LineChart data={claimedNotClaimed} margin={{ top: 20, right: 30, left: 20, bottom: 40 }}>
												<CartesianGrid strokeDasharray="3 3" />
												<XAxis
													dataKey={'name'}
													angle={-45}
													textAnchor="end"
													fontSize={12}
													interval={timeRanges.claimedNotClaimed === 'pastQuarter' ? 2 : 0}
													tickFormatter={value => {
														const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
														const capitalized = truncated
															.toLowerCase()
															.split(' ')
															.map(word => word.charAt(0).toUpperCase() + word.slice(1))
															.join(' ');
														return capitalized;
													}}
												/>
												<YAxis domain={[0, 'auto']} />

												<Tooltip content={<CustomTooltip />} />
												<Legend wrapperStyle={{ marginBottom: '-45px' }} />
												<Line type="monotone" dataKey="claimed" stroke="#8884d8" strokeWidth={2} name="Claimed" connectNulls>
													<LabelList
														dataKey="claimed"
														position="top"
														style={{ fontSize: '12px', fill: '#333' }}
														formatter={value => `${value}`}
														content={renderCustomLabelAlt}
													/>
												</Line>
												<Line type="monotone" dataKey="notClaimed" stroke="#ff6e54" strokeWidth={2} name="Not Claimed" connectNulls>
													<LabelList
														dataKey="notClaimed"
														position="top"
														style={{ fontSize: '12px', fill: '#333' }}
														formatter={value => `${value}`}
														content={renderCustomLabelAlt}
													/>
												</Line>
											</LineChart>
										</ResponsiveContainer>
									</div>
								) : (
									<div className=" w-100 card d-flex justify-content-center align-items-center" style={{ height: '50px' }}>
										<p className=" mt-6" style={{ paddingTop: '50px' }}>
											No Record for the selected time
										</p>
									</div>
								)}
							</div>
						</div>
					</div>
				</div>

				{/* top services by hmo */}
				<div className="row">
					<div className="col-12">
						<div className="element-wrapper">
							<h6 className="element-header">Top 10 HMO services by Payments </h6>
							<div className="d-flex justify-content-end w-100">
								<div className="d-flex justify-content-end w-100">
									<div>
										<div
											style={{ width: '100px', cursor: 'pointer', textAlign: 'center' }}
											onClick={() => setModalAlt(true)}
											className="form-control form-control-sm text-white bg-primary rounded"
										>
											View All
										</div>
									</div>
									&nbsp;
									<TimeRangeDropdown
										value={timeRanges.topBottomServices}
										onChange={handleTimeRangeChange}
										chartId="topBottomServicesPayment"
									/>
								</div>
							</div>
							<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
								Top 5 most paid HMO services, highlighting high-demand areas.
							</p>
							<div className="element-box">
								{loadingStates.topBottomServices ? (
									<TableLoading />
								) : topServicesPayment.length > 0 ? (
									<div style={{ width: '100%', height: 400 }}>
										<ResponsiveContainer>
											<BarChart data={topServicesPayment?.slice(0, 10)} margin={{ top: 20, right: 30, left: 20, bottom: 40 }}>
												<CartesianGrid strokeDasharray="3 3" />
												<XAxis
													dataKey={'name'}
													angle={-45}
													textAnchor="end"
													fontSize={12}
													interval={0}
													tickFormatter={value => {
														const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
														const capitalized = truncated
															.toLowerCase()
															.split(' ')
															.map(word => word.charAt(0).toUpperCase() + word.slice(1))
															.join(' ');
														return capitalized;
													}}
												/>
												<YAxis domain={[0, 'auto']} />
												<Tooltip
													formatter={value => Number(value).toLocaleString()}
													labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
												/>
												{/* <Tooltip content={<CustomTooltip />} /> */}
												<Legend wrapperStyle={{ marginBottom: '-45px' }} />
												<Bar dataKey="value" fill="#8884d8" name="Payments/Transactions">
													{topServicesPayment.map((entry, index) => (
														<Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
													))}
													<LabelList
														dataKey="value"
														position="top"
														style={{ fontSize: '12px', fill: '#333' }}
														// formatter={value => `${value}`}
														content={formatBarLabel}
													/>
												</Bar>
											</BarChart>
										</ResponsiveContainer>
									</div>
								) : (
									<div className=" w-100 card d-flex justify-content-center align-items-center" style={{ height: '50px' }}>
										<p className=" mt-6" style={{ paddingTop: '50px' }}>
											No Record for the selected time
										</p>
									</div>
								)}
							</div>
						</div>
					</div>

					<div className="col-12">
						<div className="element-wrapper">
							<h6 className="element-header">Bottom 10 HMO Services by payments</h6>
							<div className="d-flex justify-content-end w-100">
								<div className="d-flex justify-content-end w-100">
									<div>
										<div
											style={{ width: '100px', cursor: 'pointer', textAlign: 'center' }}
											onClick={() => setModalAlt(true)}
											className="form-control form-control-sm text-white bg-primary rounded"
										>
											View All
										</div>
									</div>
									&nbsp;
									<TimeRangeDropdown
										value={timeRanges.topBottomServices}
										onChange={handleTimeRangeChange}
										chartId="topBottomServicesPayment"
									/>
								</div>
							</div>
							<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
								Least paid HMO services, identifying areas for potential improvement.
							</p>
							<div className="element-box">
								{loadingStates.topBottomServices ? (
									<TableLoading />
								) : bottomServicesPayment.length > 0 ? (
									<div style={{ width: '100%', height: 400 }}>
										<ResponsiveContainer>
											<BarChart data={bottomServicesPayment?.slice(-10)} margin={{ top: 20, right: 30, left: 20, bottom: 40 }}>
												<CartesianGrid strokeDasharray="3 3" />
												<XAxis
													dataKey={'name'}
													angle={-45}
													textAnchor="end"
													fontSize={12}
													interval={0}
													tickFormatter={value => {
														const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
														const capitalized = truncated
															.toLowerCase()
															.split(' ')
															.map(word => word.charAt(0).toUpperCase() + word.slice(1))
															.join(' ');
														return capitalized;
													}}
												/>
												<YAxis domain={[0, 'auto']} />
												<Tooltip
													formatter={value => Number(value).toLocaleString()}
													labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
												/>

												<Legend wrapperStyle={{ marginBottom: '-45px' }} />
												<Bar dataKey="value" fill="#ff6e54" name="Payments/Transactions">
													{bottomServicesPayment.map((entry, index) => (
														<Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
													))}
													<LabelList
														dataKey="value"
														position="top"
														style={{ fontSize: '12px', fill: '#333' }}
														formatter={value => `${value}`}
														content={formatBarLabel}
													/>
												</Bar>
											</BarChart>
										</ResponsiveContainer>
									</div>
								) : (
									<div className=" w-100 card d-flex justify-content-center align-items-center" style={{ height: '50px' }}>
										<p className=" mt-6" style={{ paddingTop: '50px' }}>
											No Record for the selected time
										</p>
									</div>
								)}
							</div>
						</div>
						<SlidingPane isOpen={modalAlt}>
							<div className=" content-box">
								<div className=" d-flex justify-content-end w-100">
									<button className="btn btn-danger float-end" onClick={() => setModalAlt(false)}>
										&times;
									</button>
								</div>

								<div className=" d-flex justify-content-end mt-5 t-5 align-items-center">
									<TimeRangeDropdown
										value={timeRanges.topBottomServices}
										onChange={handleTimeRangeChange}
										chartId="topBottomServicesPayment"
									/>
								</div>
								<div className="row  w-100 " style={{ paddingLeft: '50px', paddingRight: '50px' }}>
									<div className="col-sm-12">
										<div className="element-wrapper">
											<h6 className="element-header">HMO Services By Payment </h6>

											<p className="chart-description card p-2 " style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
												High-level metrics showing overall metrics of reading over time.
											</p>
											<div className="element-box">
												{/* <TableLoading /> */}

												<Row gutter={16}>
													<Col span={8}>
														<Card>
															<Statistic title="Total" value={bottomServicesPayment?.length} suffix="" />
															<small className="text-muted">Total HMO Services</small>
														</Card>
													</Col>
													{/* <Col span={8}>
														<Card>
															<Statistic
																title="Total Revenue"
																value={transSummary?.totalRevenueFormatted} suffix=""
															/>

															<small className="text-muted">Cumulative Revenue From --- to --</small>
														</Card>
													</Col>
													<Col span={8}>
														<Card>
															<Statistic
																title="Total Transactions"
																value={transSummary?.totalTransactions} suffix=""
															/>
															<Statistic
																					title="Survey Coverage Rate"
																					value={coverageRate > 0 ? (coverageRate).toFixed(1) : 0}
																					suffix=""
																				/>
															<small className="text-muted">Cumulative Transactions From --- to --</small>
														</Card>
													</Col> */}
													{/* <Col span={6}>
																		<Card>
																			<Statistic title="Lowest Scoring Area" value={0} suffix="/3" />
																			<small className="text-muted">Needs attention</small>
																		</Card>
																	</Col> */}
												</Row>
											</div>
										</div>
									</div>
								</div>

								<div className=" " style={{ marginLeft: '50px', marginRight: '50px' }}>
									<p className="chart-description card p-2">
										This Chart highlights paid HMO services, identifying areas for potential improvement.
									</p>
								</div>

								<div className=" " style={{ marginLeft: '50px', marginRight: '50px' }}>
									<div className=" card w-100 ">
										<div className="row">
											<div className="col-md-12 mb-4">
												<div className="card h-100">
													<div className="card-body">
														{loadingStates.topBottomServices ? (
															<TableLoading />
														) : bottomServicesPayment.length > 0 ? (
															<div style={{ width: '100%', height: 400 }}>
																<ResponsiveContainer>
																	<BarChart data={bottomServicesPayment} margin={{ top: 20, right: 30, left: 20, bottom: 40 }}>
																		<CartesianGrid strokeDasharray="3 3" />
																		<XAxis
																			dataKey={'name'}
																			angle={-45}
																			textAnchor="end"
																			fontSize={12}
																			interval={0}
																			tickFormatter={value => {
																				const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
																				const capitalized = truncated
																					.toLowerCase()
																					.split(' ')
																					.map(word => word.charAt(0).toUpperCase() + word.slice(1))
																					.join(' ');
																				return capitalized;
																			}}
																		/>
																		<YAxis domain={[0, 'auto']} />
																		<Tooltip
																			formatter={value => Number(value).toLocaleString()}
																			labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
																		/>

																		<Legend wrapperStyle={{ marginBottom: '-45px' }} />
																		<Bar dataKey="value" fill="#ff6e54" name="Payments/Transactions">
																			{bottomServicesPayment.map((entry, index) => (
																				<Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
																			))}
																			<LabelList
																				dataKey="value"
																				position="top"
																				style={{ fontSize: '12px', fill: '#333' }}
																				formatter={value => `${value}`}
																				content={formatBarLabel}
																			/>
																		</Bar>
																	</BarChart>
																</ResponsiveContainer>
															</div>
														) : (
															<div className=" w-100 card d-flex justify-content-center align-items-center" style={{ height: '50px' }}>
																<p className=" mt-6" style={{ paddingTop: '50px' }}>
																	No Record for the selected time
																</p>
															</div>
														)}
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</SlidingPane>
					</div>
				</div>
			</div>
		</div>
	);
};

export default StaffHmoDashboard;
